import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import GroupsIcon from '@mui/icons-material/Groups';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import config from '../config';
import RuleScreen from '../shared/RuleScreen';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import DetailDrawer from '../shared/DetailDrawer';
import { buildParams } from '../shared/apiParams';

const API_URL = config.apiUrl;
const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '' };

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  if (isNaN(d.getTime())) return 'Invalid Date';
  const p = (n) => n.toString().padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const calcDuration = (start, end) => {
  if (!start || !end) return 'N/A';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms)) return 'Invalid';
  const min = Math.abs(ms / 60000);
  const h = Math.floor(min / 60);
  const m = Math.floor(min % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
];

// Keys must match the backend METRICS keys (controllers/priorityIndexController.js).
// There is no "Not Classified" bucket — any household that isn't High or
// Medium is counted as Low Priority.
const METRICS = [
  { key: 'high', label: 'High Priority', icon: PriorityHighIcon, accent: 'var(--danger)' },
  { key: 'medium', label: 'Medium Priority', icon: ReportProblemIcon, accent: 'var(--warning)' },
  { key: 'low', label: 'Low Priority', icon: CheckCircleIcon, accent: 'var(--success)' },
];

const pctCell = (b, accent) => (
  <>
    {b?.count || 0}
    <span style={{ fontSize: '0.72rem', color: accent || 'var(--muted)', fontWeight: 700, marginLeft: 4 }}>
      ({b?.pct || 0}%)
    </span>
  </>
);

const priorityBadgeClass = (level) => {
  if (level === 'High') return 'badge-danger';
  if (level === 'Medium') return 'badge-warning';
  if (level === 'Low') return 'badge-success';
  return 'badge-neutral';
};

export default function FamilyPriorityIndex() {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, metric: null, records: [], loading: false });
  const [recordPopup, setRecordPopup] = useState({ open: false, record: null });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      if (activeTab !== 'enumerator') params.append('forceGroupBy', activeTab);
      const res = await axios.get(`${API_URL}/priority-index/analytics?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching family priority index data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalHouseholds = summary?.totalHouseholds ?? rows.reduce((s, r) => s + (Number(r.totalHouseholds) || 0), 0);

  const tiles = (
    <>
      <StatTile label="Total Households" value={totalHouseholds} icon={GroupsIcon} accent="var(--primary)" />
      {METRICS.map((m) => (
        <StatTile
          key={m.key}
          label={m.label}
          value={`${summary?.[m.key]?.pct ?? 0}%`}
          icon={m.icon}
          accent={m.accent}
          sub={`${summary?.[m.key]?.count ?? 0} of ${totalHouseholds} households`}
        />
      ))}
    </>
  );

  const openDrilldown = async (row, metric = null) => {
    setDrawer({ open: true, row, metric, records: [], loading: true });
    try {
      const params = buildParams(applied, {
        extra: { groupBy: activeTab, groupValue: row.name, filter: metric?.key, limit: 100 },
      });
      const res = await axios.get(`${API_URL}/priority-index/details?${params}`);
      const records = res.data?.data || [];
      setDrawer({ open: true, row, metric, records, loading: false });
    } catch (err) {
      console.error('Error fetching family priority index detail records:', err);
      setDrawer({ open: true, row, metric, records: [], loading: false });
    }
  };

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{r.name || 'N/A'}</span> },
    { key: 'totalHouseholds', label: 'Total Household', align: 'center', render: (r) => <span className="cell-strong">{r.totalHouseholds || 0}</span> },
    ...METRICS.map((m) => ({
      key: m.key,
      label: m.label,
      align: 'center',
      render: (r) => (
        <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openDrilldown(r, m); }}>
          {pctCell(r[m.key], m.accent)}
        </span>
      ),
    })),
  ];

  const drawerRecordColumns = [
    { key: 'srNo', label: 'Sr No', align: 'center', render: (_r, i) => i + 1 },
    { key: 'household_id', label: 'Household ID', render: (r) => r.household_id || '—' },
    { key: 'respondent_name', label: 'Respondent', render: (r) => r.respondent_name || '—' },
    {
      key: 'priorityLevel',
      label: 'Priority',
      render: (r) => <span className={`badge ${priorityBadgeClass(r.priorityLevel)}`}>{r.priorityLevel}</span>,
    },
    { key: 'income', label: 'Annual Income', render: (r) => r.income || '—' },
    { key: 'houseType', label: 'House Type', render: (r) => r.houseType || '—' },
    { key: 'toilet', label: 'Toilet', render: (r) => r.toilet || '—' },
    { key: 'pipeWater', label: 'Piped Water', render: (r) => r.pipeWater || '—' },
    { key: 'block', label: 'Block', render: (r) => r.block || '—' },
    { key: 'village_name', label: 'Village', render: (r) => r.village_name || '—' },
    { key: 'start', label: 'Start Time', render: (r) => formatDate(r.start) },
    { key: 'end', label: 'End Time', render: (r) => formatDate(r.end) },
    { key: 'duration', label: 'Duration', align: 'center', render: (r) => calcDuration(r.start, r.end) },
  ];

  const filterBar = (
    <FilterBar
      onApply={(v) => { setApplied(v); setPage(1); }}
      onClear={() => { setApplied(EMPTY); setPage(1); }}
      loading={loading}
    />
  );

  return (
    <RuleScreen
      title="Family Priority Index"
      subtitle="High: income below ₹1L with infrastructure, welfare and health gaps. Medium: income above ₹1L missing a pacca house, piped water or a toilet. Low: every other household — including income above ₹2L with a pacca house, toilet, smartphone and internet."
      icon={PriorityHighIcon}
      backTo="/family-dashboard"
      backLabel="Back to Family Dashboard"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {filterBar}
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Family Priority Index by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          onRowClick={(r) => openDrilldown(r, null)}
          getRowKey={(r, i) => `${r.name}-${i}`}
        />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${drawer.row.name} — ${drawer.metric ? drawer.metric.label : 'All Households'}` : ''}
        sections={
          drawer.row
            ? [
                {
                  title: 'Breakdown',
                  items: [
                    { label: 'Total Households', value: drawer.row.totalHouseholds || 0 },
                    ...METRICS.map((m) => ({
                      label: m.label,
                      value: `${drawer.row[m.key]?.count || 0} (${drawer.row[m.key]?.pct || 0}%)`,
                    })),
                  ],
                },
              ]
            : []
        }
      >
        <div className="detail-section">
          <h4>Households</h4>
          <DataTable
            columns={drawerRecordColumns}
            rows={drawer.records}
            loading={drawer.loading}
            emptyMessage="No households found."
            onRowClick={(r) => setRecordPopup({ open: true, record: r })}
            getRowKey={(r, i) => r.uuid || i}
          />
        </div>
      </DetailDrawer>

      <DetailDrawer
        open={recordPopup.open}
        onClose={() => setRecordPopup((p) => ({ ...p, open: false }))}
        title={recordPopup.record ? `Household: ${recordPopup.record.household_id}` : ''}
        sections={
          recordPopup.record
            ? [
                {
                  title: 'Status',
                  items: [
                    {
                      label: 'Priority Level',
                      value: (
                        <span className={`badge ${priorityBadgeClass(recordPopup.record.priorityLevel)}`}>
                          {recordPopup.record.priorityLevel}
                        </span>
                      ),
                    },
                  ],
                },
                {
                  title: 'Household',
                  items: [
                    { label: 'Household ID', value: recordPopup.record.household_id },
                    { label: 'Respondent', value: recordPopup.record.respondent_name },
                    { label: 'Annual Income', value: recordPopup.record.income },
                    { label: 'House Type', value: recordPopup.record.houseType },
                    { label: 'Toilet', value: recordPopup.record.toilet },
                    { label: 'Piped Water', value: recordPopup.record.pipeWater },
                    { label: 'LPG', value: recordPopup.record.lpg },
                    { label: 'Health Insurance', value: recordPopup.record.healthInsurance },
                    { label: 'Internet Access', value: recordPopup.record.internetAccess },
                    { label: 'Chronic Illness', value: recordPopup.record.hasChronicIllness ? 'Yes' : 'No' },
                    { label: 'Disability', value: recordPopup.record.hasDisability ? 'Yes' : 'No' },
                    { label: 'Smartphone User', value: recordPopup.record.hasSmartphoneUser ? 'Yes' : 'No' },
                    { label: 'Subhadra Eligible', value: recordPopup.record.subhadraEligible },
                    { label: 'Subhadra Beneficiary', value: recordPopup.record.subhadraBeneficiary },
                    { label: 'Enumerator', value: recordPopup.record.enumerator_id },
                    { label: 'Block', value: recordPopup.record.block },
                    { label: 'Gram Panchayat', value: recordPopup.record.gram_panchayat },
                    { label: 'Village', value: recordPopup.record.village_name },
                  ],
                },
                {
                  title: 'Survey Timing',
                  items: [
                    { label: 'Start Time', value: formatDate(recordPopup.record.start) },
                    { label: 'End Time', value: formatDate(recordPopup.record.end) },
                    { label: 'Duration', value: calcDuration(recordPopup.record.start, recordPopup.record.end) },
                  ],
                },
              ]
            : []
        }
      />
    </RuleScreen>
  );
}
